import { map } from "rxjs/operators";
import { Injectable } from "@angular/core";
import { environment } from "environments/environment";
import { Observable } from "rxjs";

import { OrganizationMapper } from "./organization.mapper";
import {
  CarrierAccountReveal,
  CarrierConnectionResult,
  CarrierIntegrationState,
  CarrierProvider,
} from "./carrier-integration.model";
import { Organization } from "./organization.model";
import { CrudAbstractRepository } from "../crud-abstract/crud-abstract.repository";

@Injectable()
export class OrganizationRepository extends CrudAbstractRepository {
  getMapper(): any {
    return OrganizationMapper.map;
  }

  getEndpoint(): string {
    return "organization";
  }

  public list(): Observable<Organization[]> {
    return this.http
      .get<object[]>(`${environment.apiUrl}/organization/list`)
      .pipe(map((response) => response.map(this.getMapper())));
  }

  public getOrganization(id: number): Observable<Organization> {
    return this.http
      .get(`${environment.apiUrl}/organization/${id}`)
      .pipe(map(this.getMapper()));
  }

  // Sends only the given fields; the rest of the organization stays as stored.
  public updateFields(
    id: number,
    fields: Record<string, unknown>,
  ): Observable<Organization> {
    return this.http
      .patch(`${environment.apiUrl}/organization/${id}`, fields)
      .pipe(map(this.getMapper()));
  }

  // Super admin only; bonus credits carry over to the next month.
  public addBonusCredits(
    id: number,
    credits: number,
  ): Observable<Organization> {
    return this.http
      .post(`${environment.apiUrl}/organization/${id}/credits/bonus`, {
        credits,
      })
      .pipe(map(this.getMapper()));
  }

  // The backend prices the package; purchased credits are billed at the end of the month.
  public purchaseCredits(
    id: number,
    credits: number,
  ): Observable<Organization> {
    return this.http
      .post(`${environment.apiUrl}/organization/${id}/credits/purchase`, {
        credits,
      })
      .pipe(map(this.getMapper()));
  }

  // Stores the pasted carrier token (when given) and asks the carrier once,
  // so the admin learns on the spot whether the paste worked.
  public testCarrierConnection(
    id: number,
    provider: CarrierProvider,
    token: string | null = null,
  ): Observable<CarrierConnectionResult> {
    return this.http.post<CarrierConnectionResult>(
      this.carrierUrl(id, provider, "connection"),
      token ? { token } : {},
    );
  }

  // Drops the connection and the handed-over account.
  public disconnectCarrier(
    id: number,
    provider: CarrierProvider,
  ): Observable<CarrierIntegrationState> {
    return this.http.delete<CarrierIntegrationState>(
      this.carrierUrl(id, provider, "connection"),
    );
  }

  // The browser worker logs in with the stored account; the connection lands asynchronously.
  public connectCarrierAutomatically(
    id: number,
    provider: CarrierProvider,
  ): Observable<CarrierIntegrationState> {
    return this.http.post<CarrierIntegrationState>(
      this.carrierUrl(id, provider, "connection/automated"),
      {},
    );
  }

  // The customer hands over their carrier account; the browser worker or support logs in with it.
  public storeCarrierAccount(
    id: number,
    provider: CarrierProvider,
    login: string,
    password: string,
  ): Observable<CarrierIntegrationState> {
    return this.http.post<CarrierIntegrationState>(
      this.carrierUrl(id, provider, "account"),
      { login, password },
    );
  }

  public clearCarrierAccount(
    id: number,
    provider: CarrierProvider,
  ): Observable<CarrierIntegrationState> {
    return this.http.delete<CarrierIntegrationState>(
      this.carrierUrl(id, provider, "account"),
    );
  }

  // Super admin only; every call is stamped on the organization.
  public revealCarrierAccount(
    id: number,
    provider: CarrierProvider,
  ): Observable<CarrierAccountReveal> {
    return this.http.post<CarrierAccountReveal>(
      this.carrierUrl(id, provider, "account/reveal"),
      {},
    );
  }

  private carrierUrl(
    id: number,
    provider: CarrierProvider,
    path: string,
  ): string {
    return `${environment.apiUrl}/organization/${id}/integration/${provider}/${path}`;
  }
}
